<template>
  <div>
    <v-card class="filter-card mt-3 mx-4" outlined>
      <div class="filter-card-header" @click="filterPanelExpanded = !filterPanelExpanded">
        <span class="filter-card-title">篩選條件</span>
        <v-icon>{{ filterPanelExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
      </div>
      <v-expand-transition>
        <div v-show="filterPanelExpanded">
          <div class="filter-row">
            <v-text-field
              v-model="pendingSearch"
              placeholder="姓名／手機／生日（民國例801231）"
              outlined
              hide-details
              dense
              clearable
              style="max-width: 280px;"
              @keydown.enter="applyFilter"
            />
            <v-select
              v-model="pendingCareFilter"
              :items="careTypeOptions"
              prefix="關懷類型："
              dense
              outlined
              hide-details
              style="width: 200px;"
            />
            <v-select
              v-model="pendingCareStatus"
              :items="careStatusOptions"
              prefix="患者狀態："
              dense
              outlined
              hide-details
              style="width: 200px;"
            />
            <v-spacer />
            <v-btn color="primary" depressed @click="applyFilter">套用篩選</v-btn>
            <v-btn outlined @click="clearFilter">清除篩選</v-btn>
          </div>
        </div>
      </v-expand-transition>
    </v-card>
    <transition name="floating-bar">
      <div v-if="selected.length > 0" class="floating-action-bar mx-4">
        <div class="floating-action-bar__left">
          已選 <b>{{ selected.length }}</b> 筆／共 <b>{{ totalCares }}</b> 筆
        </div>
        <div class="floating-action-bar__right">
          <v-btn text small :disabled="batchSubmitting" @click="selected = []">取消選取</v-btn>
          <div class="floating-action-bar__divider" />
          <v-btn
            :disabled="batchSubmitting"
            color="primary"
            depressed
            @click="confirmDialog = true"
          >
            {{ batchActionText }}（{{ selected.length }}）
          </v-btn>
        </div>
      </div>
    </transition>
    <FloatingHorizontalScrollbar ref="floatingScrollbar">
      <v-data-table
        v-model="selected"
        :headers="headers"
        :items="careItems"
        item-key="id"
        show-select
        :item-class="rowClass"
        :options.sync="options"
        :loading="loading"
        :server-items-length="totalCares"
        :footer-props="{itemsPerPageOptions: [10, 20, 50, 100]}"
      >
        <template v-slot:item.birthday="{ item }">
          <template v-if="item.birthday">{{ parseInt(item.birthday) * 1000 | formatROCDate }}</template>
        </template>
        <template v-slot:item.content="{ item }">
          <span v-if="item.content && item.content.length > 8">
            {{ item.content.substr(0, 8) }}
            <a @click="showMoreContent(item.content)">...more</a>
          </span>
          <span v-else>
            {{ item.content }}
          </span>
        </template>
        <template v-slot:item.clinic_call="{ item }">
          <span v-if="parseInt(item.clinic_call)" style="color: var(--v-red-6-base);">請聯繫患者</span>
          <span v-else style="color: var(--v-grey-5-base);">不需要聯繫</span>
        </template>
        <template v-slot:item.care_note="{ item }">
          <span>
            {{ (item.care_note && item.care_note.length > 20) ? item.care_note.substr(0, 20) + '...' : item.care_note }}
          </span>
          <v-btn icon color="primary" @click="handleEdit(item)">
            <v-icon dark x-small>
              mdi-pencil
            </v-icon>
          </v-btn>
        </template>
        <template v-slot:item.action="{ item }">
          <v-btn v-if="status === 0" outlined small color="primary" style="margin: 5px 0;" @click="changeStatus(item, 1)">
            設為已處理
          </v-btn>
          <v-btn v-else outlined small color="primary" style="margin: 5px 0;"
                 @click="changeStatus(item, 0)">
            設為未處理
          </v-btn>
        </template>

        <template v-slot:item.send="{ item }">
          <SendMessageChannelMenu
            :patient="{
              patientName: item.name,
              patientCode: item.patient_code,
              patientPhone: item.phone,
            }"
          />
        </template>
      </v-data-table>
    </FloatingHorizontalScrollbar>
    <v-dialog v-model="confirmDialog" max-width="480" persistent>
      <v-card>
        <v-card-title class="text-h6">確認批次移動</v-card-title>
        <v-card-text class="text-body-2">
          已選擇 <b>{{ selected.length }}</b> 筆關懷紀錄，即將移動至「<b>{{ targetTabText }}</b>」分頁<br>
          若需復原，可前往{{ targetTabText }}分頁，勾選後點擊「{{ reverseActionText }}」
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="batchSubmitting" @click="confirmDialog = false">取消</v-btn>
          <v-btn color="primary" :loading="batchSubmitting" depressed @click="confirmBatchMove">確認</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-if="reportBackNoteDialog" max-width="500" v-model="reportBackNoteDialog" persistent>
      <v-card>
        <v-card-title>編輯術後備註</v-card-title>
        <v-card-text>
          <v-textarea v-model="currentEdit.care_note" hide-details outlined no-resize/>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue" text @click="editReportBackNote">
            ok
          </v-btn>
          <v-btn text @click="reportBackNoteDialog=false">
            {{ $t('GENERAL.cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-if="showMoreDialog" v-model="showMoreDialog" width="fit-content">
      <v-card width="fit-content" max-width="350" max-height="50vh" class="pa-6 text-pre-line overflow-y-auto" v-text="moreContent"/>
    </v-dialog>
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000" centered>
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import {
  batchReplyCareAPI,
  batchUnreplyCareAPI,
  getCareAPI,
  updateCareAPI
} from "@/api";
import FloatingHorizontalScrollbar from "@/components/common/FloatingHorizontalScrollbar.vue";
import SendMessageChannelMenu from "@/components/utils/SendMessageChannelMenu.vue";

const DEFAULT_CARE_FILTER = 'all'
const DEFAULT_CARE_STATUS = '2'

export default {
  name: "HistoryList",
  components: {FloatingHorizontalScrollbar, SendMessageChannelMenu},
  props: {
    status: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      careItems: [],
      options: {},
      loading: true,
      totalCares: 0,
      currentEdit: null,
      reportBackNoteDialog: false,
      showMoreDialog: false,
      moreContent: null,

      filterPanelExpanded: false,
      pendingSearch: null,
      pendingCareFilter: DEFAULT_CARE_FILTER,
      pendingCareStatus: DEFAULT_CARE_STATUS,
      appliedSearch: null,
      appliedCareFilter: DEFAULT_CARE_FILTER,
      appliedCareStatus: DEFAULT_CARE_STATUS,

      selected: [],
      batchSubmitting: false,
      confirmDialog: false,
      snackbar: {
        show: false,
        text: '',
        color: 'success',
      },

      careTypeOptions: [
        { text: '全部', value: 'all' },
        { text: '術前關懷', value: 'pre' },
        { text: '術後關懷', value: 'post' },
      ],
      careStatusOptions: [
        { text: '全部', value: 'all' },
        { text: '有疑問', value: '2' },
        { text: '無疑問', value: '1' },
      ],
    }
  },
  computed: {
    ...mapGetters([
      'axiosParamData',
      'liffUser',
      'doctorUser'
    ]),
    headers() {
      const headers = [
        {text: this.$t('PostOperativeCare.patientName'), align: 'center', value: 'name', sortable: false, width: 100},
        {text: this.$t('PostOperativeCare.patientPhone'), align: 'center', value: 'phone', sortable: false, width: 120},
        {text: this.$t('ReserveTable.patientBirth'), align: 'center', value: 'birthday', sortable: false, width: 132},
        {text: this.$t('PostOperativeCare.reserveTime'), align: 'center', value: 'start_time', sortable: false, width: 120},
        {
          text: this.$t('PostOperativeCare.reserveDoctor'),
          align: 'center',
          value: 'doctor_name',
          sortable: false,
          width: 100
        },
        {text: this.$t('PostOperativeCare.reserveNote'), align: 'center', value: 'note', sortable: false, width: 150},
        {
          text: this.$t('PostOperativeCare.reportBackTime'),
          align: 'center',
          value: 'create_time',
          sortable: false,
          width: 150
        },
        {text: '患者點擊按鈕', align: 'center', value: 'care_status_text', sortable: false, width: 150},
        {text: this.$t('PostOperativeCare.reportBack'), align: 'center', value: 'content', sortable: false, width: 120},
        {
          text: this.$t('PostOperativeCare.needContact'),
          align: 'center',
          value: 'clinic_call',
          sortable: false,
          width: 150
        },
        {
          text: this.$t('PostOperativeCare.reportBackNote'),
          align: 'center',
          value: 'care_note',
          sortable: false,
          width: 150
        },
        {
          text: this.$t('PostOperativeCare.action'),
          align: 'center',
          value: 'action',
          sortable: false,
          width: 150
        },
      ]
      if (this.doctorUser)
        headers[3] = {
          text: this.$t('PostOperativeCare.reserveClinic'),
          align: 'center',
          value: 'clinic_name',
          sortable: false,
          width: 100
        }

      headers.push({text: '傳送訊息', align: 'center', value: 'send', sortable: false, width: 150})
      return headers
    },
    batchActionText() {
      return this.status === 0 ? '移至已處理' : '移回待處理'
    },
    targetTabText() {
      return this.status === 0 ? '已處理' : '待處理'
    },
    reverseActionText() {
      return this.status === 0 ? '移回待處理' : '移至已處理'
    },
  },
  watch: {
    options: {
      handler() {
        this.getDataFromApi();
      },
      deep: true,
    },
    status: {
      handler() {
        this.selected = []
        if (this.options.page === 1) {
          this.getDataFromApi();
        } else {
          this.options.page = 1;
        }
      },
      immediate: true, // Fetch data when component is created
    },
    filterPanelExpanded() {
      this.refreshFloatingScrollbar()
    },
  },
  methods: {
    ...mapActions({
      getUnreadNotice: 'getUnreadNotice',
    }),
    refreshFloatingScrollbar() {
      if (this.$refs.floatingScrollbar) {
        this.$refs.floatingScrollbar.refresh()
      }
    },
    applyFilter() {
      this.appliedSearch = this.pendingSearch
      this.appliedCareFilter = this.pendingCareFilter
      this.appliedCareStatus = this.pendingCareStatus
      this.selected = []
      if (this.options.page === 1) this.getDataFromApi()
      else this.options.page = 1
    },
    clearFilter() {
      this.pendingSearch = null
      this.pendingCareFilter = DEFAULT_CARE_FILTER
      this.pendingCareStatus = DEFAULT_CARE_STATUS
      this.applyFilter()
    },
    rowClass(item) {
      return this.selected.some(s => s.id === item.id) ? 'row-selected' : ''
    },
    showSnackbar(text, color = 'success') {
      this.snackbar.text = text
      this.snackbar.color = color
      this.snackbar.show = true
    },
    async confirmBatchMove() {
      if (!this.selected.length) return
      this.batchSubmitting = true
      const ids = this.selected.map(s => Number(s.id))
      const payload = { ids, ...this.axiosParamData }
      const apiCall = this.status === 0 ? batchReplyCareAPI : batchUnreplyCareAPI
      try {
        const res = await apiCall(payload)
        const requested = res?.data?.requested ?? ids.length
        const affected = res?.data?.affected ?? 0
        if (affected === requested) {
          this.showSnackbar(`成功移動 ${affected} 筆`, 'success')
        } else {
          const skipped = requested - affected
          this.showSnackbar(`成功移動 ${affected} 筆，${skipped} 筆略過（資料已是目標狀態或不在權限範圍）`, 'warning')
        }
        this.confirmDialog = false
        this.selected = []
        this.getDataFromApi()
        this.getUnreadNotice()
      } catch (e) {
        this.showSnackbar('操作失敗，請稍後再試', 'error')
      } finally {
        this.batchSubmitting = false
      }
    },
    getDataFromApi() {
      this.loading = true
      this.selected = []
      const {page, itemsPerPage} = this.options
      getCareAPI({
        ...this.axiosParamData,
        reply_status: this.status,
        limit: itemsPerPage,
        page,
        name: this.appliedSearch,
        care_type: this.appliedCareFilter !== 'all' ? this.appliedCareFilter : undefined,
        care_status: this.appliedCareStatus
      }).then(res => {
        this.careItems = res.data.data
        this.totalCares = parseInt(res.data.paginate.total)
        this.loading = false
        this.refreshFloatingScrollbar()
      })
    },
    handleEdit(item) {
      this.currentEdit = JSON.parse(JSON.stringify(item))
      this.reportBackNoteDialog = true
    },
    editReportBackNote() {
      updateCareAPI(this.currentEdit.id, {care_note: this.currentEdit.care_note, ...this.axiosParamData}).then(() => {
        this.currentEdit = null
        this.reportBackNoteDialog = false
        this.getDataFromApi()
      })
    },
    showMoreContent(v) {
      this.moreContent = v
      this.showMoreDialog = true
    },
    changeStatus(item, v) {
      updateCareAPI(item.id, {reply_status: v, ...this.axiosParamData}).then(() => {
        this.getDataFromApi()
        this.getUnreadNotice()
      })
    }
  }
}
</script>

<style scoped>
.filter-card {
  border-radius: 4px;
}
.filter-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  cursor: pointer;
  user-select: none;
  border-bottom: 1px solid #F0F0F0;
}
.filter-card-title {
  font-size: 14px;
  font-weight: 600;
  color: #424242;
}
.filter-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  flex-wrap: wrap;
}
.floating-action-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #FFFFFF;
  border: 1px solid #E0E0E0;
  border-radius: 4px;
  padding: 8px 16px;
  margin-top: 8px;
  position: sticky;
  top: 8px;
  z-index: 2;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}
.floating-action-bar__left {
  font-size: 14px;
  color: #424242;
}
.floating-action-bar__right {
  display: flex;
  align-items: center;
  gap: 12px;
}
.floating-action-bar__divider {
  width: 1px;
  height: 24px;
  background-color: #E0E0E0;
}
.floating-bar-enter-active,
.floating-bar-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.floating-bar-enter,
.floating-bar-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
::v-deep .row-selected td {
  background-color: #E6FAF9 !important;
}
::v-deep .v-text-field--outlined.v-input--dense .v-text-field__prefix {
  color: #757575;
  margin-top: 0;
}
</style>
